import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Un cliente pendiente de respuesta esta semana (B.9). */
export interface ClientePendiente {
  contactId: string
  nombre: string
  telefono: string
  semanasSinPedir: number
  aplazamientos: number
  estadoCiclo: string | null
}

interface RespuestaPendientes {
  items: ClientePendiente[]
  total: number
}

/** Lista de no-respondedores de la semana (sección Confirmar agendamiento). */
export function usePendientes() {
  const api = useApi()
  return useQuery<RespuestaPendientes, ErrorApi>({
    queryKey: ['ciclo', 'pendientes'],
    queryFn: ({ signal }) => api.pedir<RespuestaPendientes>('/api/ciclo/pendientes', { senal: signal }),
  })
}

/** Categoría de gestión de un cliente esta semana (para los filtros de B.9). */
export type CategoriaGestion = 'pendiente' | 'agendo' | 'aplazo' | 'no_responde'

/** Un cliente de la semana con su categoría ya resuelta. */
export interface ClienteGestion extends ClientePendiente {
  categoria: CategoriaGestion
  respondioWhatsapp: boolean
}

export interface GestionSemana {
  semana: string
  clientes: ClienteGestion[]
  contadores: { pendiente: number; agendo: number; aplazo: number; no_responde: number; total: number }
}

/**
 * Vista completa de la gestión semanal: TODOS los clientes de la semana con su
 * categoría (pendiente/agendó/aplazó/no responde) y contadores. Alimenta los
 * filtros de la pantalla "Confirmar agendamiento". A diferencia de usePendientes,
 * los clientes no desaparecen al marcarlos: cambian de categoría.
 */
export function useGestionSemana() {
  const api = useApi()
  return useQuery<GestionSemana, ErrorApi>({
    queryKey: ['ciclo', 'gestion'],
    queryFn: ({ signal }) => api.pedir<GestionSemana>('/api/ciclo/gestion', { senal: signal }),
  })
}

export type ResultadoGestion = 'agendado' | 'aplazado' | 'cancelado'

/**
 * Marca el resultado de la gestión telefónica de un cliente y refresca las
 * vistas (pendientes y gestión). El cliente cambia de categoría, no desaparece.
 */
export function useMarcarPendiente() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; estado: ResultadoGestion }>({
    mutationFn: ({ contactId, estado }) =>
      api.pedir(`/api/ciclo/pendientes/${contactId}/marcar`, {
        metodo: 'POST',
        cuerpo: { estado },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ciclo', 'pendientes'] })
      void qc.invalidateQueries({ queryKey: ['ciclo', 'gestion'] })
    },
  })
}
